import { cn } from '@/lib/utils';
import { esCount } from '@/lib/es-count';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Info, X } from 'lucide-react';

export interface AdminConfirmDialogProps {
    open: boolean;
    title: string;
    description?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'default' | 'danger';
    /** confirm: Aceptar + Cancelar. alert: solo mensaje informativo (un botón Aceptar). */
    intent?: 'confirm' | 'alert';
    /** Desactiva el botón principal (p. ej. mientras hay una mutación en curso) */
    confirmDisabled?: boolean;
    /** Cuántos elementos toca la acción. El sustantivo va en plural («pedidos»). */
    affectedCount?: number;
    affectedNoun?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

function affectedPhrase(count: number, noun: string): string {
    const singular = noun.endsWith('s') ? noun.slice(0, -1) : noun;
    return `Afecta a ${esCount(count, singular, noun)}.`;
}

export function AdminConfirmDialog({
    open,
    title,
    description,
    confirmLabel,
    cancelLabel = 'Volver',
    variant = 'default',
    intent = 'confirm',
    confirmDisabled = false,
    affectedCount,
    affectedNoun = 'elementos',
    onConfirm,
    onCancel,
}: AdminConfirmDialogProps) {
    const isDanger = variant === 'danger';
    const isAlert = intent === 'alert';
    const defaultConfirm = isAlert ? 'Aceptar' : 'Confirmar';
    const resolvedConfirm = confirmLabel ?? defaultConfirm;

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onCancel();
            }}
        >
            {/*
              Tiene que ser un Dialog de Radix: el compositor ya abre uno, y ese
              pone pointer-events:none en body. Un overlay suelto se pinta encima
              pero el clic lo recibe la vista previa (el iframe del correo).
            */}
            <DialogContent
                aria-describedby={undefined}
                overlayClassName="z-[200]"
                data-admin-confirm=""
                className="z-[200] w-full max-w-md gap-0 border-border bg-card p-0 shadow-2xl sm:rounded-2xl"
            >
                <div className="flex items-start gap-3 p-5 border-b border-border/50">
                    <div
                        className={cn(
                            'p-2 rounded-xl shrink-0',
                            isDanger ? 'bg-rose-500/10' : isAlert ? 'bg-muted' : 'bg-primary/10'
                        )}
                    >
                        {isAlert ? (
                            <Info size={18} className="text-primary" />
                        ) : (
                            <AlertTriangle
                                size={18}
                                className={cn(isDanger ? 'text-rose-400' : 'text-primary')}
                            />
                        )}
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                        <DialogTitle className="text-base font-semibold leading-normal tracking-normal text-foreground">
                            {title}
                        </DialogTitle>
                        {(affectedCount != null || (description != null && description !== '')) && (
                            <div className="mt-2 text-sm text-muted-foreground space-y-2">
                                {affectedCount != null && (
                                    <p className="font-medium text-foreground">
                                        {affectedPhrase(affectedCount, affectedNoun)}
                                    </p>
                                )}
                                {description != null && description !== '' && <div>{description}</div>}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shrink-0"
                        aria-label="Volver"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className={cn('flex items-center justify-end gap-2 p-5 pt-4', isAlert && 'sm:justify-center')}>
                    {!isAlert && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        >
                            {cancelLabel}
                        </button>
                    )}
                    <button
                        type="button"
                        disabled={confirmDisabled}
                        onClick={onConfirm}
                        className={cn(
                            'px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none',
                            isDanger
                                ? 'bg-red-600 text-white hover:bg-red-700'
                                : 'bg-primary text-primary-foreground hover:bg-primary/90'
                        )}
                    >
                        {resolvedConfirm}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
